/**
 * Quality tiers + dynamic render scale (simplified from jreb-sailing's adaptive quality).
 *
 * Tier detection (research/mobile-web-graphics-research.md §6):
 *   WebGPU phone → Medium, WebGPU desktop → High, WebGL2 → Low. `?tier=` wins.
 * Dynamic render scale in [0.6, 1] of the tier DPR cap, stepping down after sustained slow frames
 * and back up after sustained fast ones (thermal-aware: never chase burst fps).
 */
import type { AbstractEngine } from '@babylonjs/core/pure';

export type QualityTier = 'low' | 'medium' | 'high';

export interface TierCaps {
  /** Max devicePixelRatio applied on top of render scale. */
  maxDpr: number;
  /** Max soldiers actually instanced (formation illusion above this). */
  maxSoldierInstances: number;
  /** Max enemies actually instanced in one block (plan 05 P5.1: was silently borrowed from
   * maxSoldierInstances at the Enemies construction site — now its own cap, same numbers). */
  maxEnemyInstances: number;
  /** Triangle budget for the frame: main pass + the explicit shadow-caster term (P5.1/P5.6).
   * The probe reports it, `tools/inspect.mjs` asserts it — source of truth for the gate table. */
  maxTriangles: number;
  /** Draw-call budget for the frame, counted across the shadow pass too (design 6). */
  maxDrawCalls: number;
  /** Bullet pool size. */
  bulletPool: number;
  /** Bridge segments kept alive. */
  bridgeSegments: number;
  /** Muzzle/impact flash pool. */
  fxParticles: number;
  /** Plan 14 P14.3: how many newest barrel explosions the debris pools draw concurrently — the
   * tier cap at the Debris construction site. Low covers the B→C chain pair (2 live blasts);
   * each blast draws ≤ SHARDS_PER_BLAST thin instances per shard pool. */
  debrisEvents: number;
  /** Plan 05 P5.6 / Design 5: real character shadows (one 1024² PCF cascade) on High only.
   * Low/Medium keep the blob shadows (plan 04 P2d). Tier is fixed at boot (Design 7), so this never
   * toggles shaders mid-run. `tools/inspect.mjs` mirrors it; quality.test.ts fails on drift. */
  realShadows: boolean;
  /** Plan 08 G1: DefaultRenderingPipeline bloom (High + Medium). Low stays pipeline-free. */
  bloom: boolean;
  /** Plan 08 G1: `pipeline.samples` (the pipeline renders off-screen, so no engine MSAA). Low's 1 is unused. */
  msaaSamples: number;
}

/**
 * Budget numbers are pinned here; `tools/inspect.mjs` mirrors them (quality.ts is the source of
 * truth — a drift between the two is a test failure in `quality.test.ts`, not a tuning task).
 * Medium's 120 k/80 are made explicit (dossier §6, conservative), Low/Medium are the plan-02 numbers.
 */
export const TIER_CAPS: Record<QualityTier, TierCaps> = {
  low: { maxDpr: 1.5, maxSoldierInstances: 24, maxEnemyInstances: 24, maxTriangles: 120_000, maxDrawCalls: 80, bulletPool: 96, bridgeSegments: 5, fxParticles: 128, debrisEvents: 2, realShadows: false, bloom: false, msaaSamples: 1 },
  medium: { maxDpr: 2, maxSoldierInstances: 36, maxEnemyInstances: 36, maxTriangles: 120_000, maxDrawCalls: 80, bulletPool: 160, bridgeSegments: 6, fxParticles: 192, debrisEvents: 3, realShadows: false, bloom: true, msaaSamples: 2 },
  high: { maxDpr: 2, maxSoldierInstances: 48, maxEnemyInstances: 48, maxTriangles: 250_000, maxDrawCalls: 250, bulletPool: 256, bridgeSegments: 7, fxParticles: 384, debrisEvents: 4, realShadows: true, bloom: true, msaaSamples: 4 },
};

const RENDER_SCALE_MIN = 0.6;
const RENDER_SCALE_STEP = 0.15;
const SLOW_FPS = 45;
const FAST_FPS = 57;
/** Frames of sustained (not burst) behaviour before scaling changes. */
const SUSTAIN_FRAMES = 90;

export function detectTier(backend: 'webgpu' | 'webgl2', params: URLSearchParams = new URLSearchParams(location.search)): QualityTier {
  // audit #18: `params` is injectable (mirrors readDebugHooks/readBootLevelIndex) so the `?tier=`
  // gate is testable without a global location.
  const url = params.get('tier');
  if (url === 'low' || url === 'medium' || url === 'high') return url;
  if (backend === 'webgl2') return 'low';
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  const mobile = nav.userAgentData?.mobile ?? /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  return mobile ? 'medium' : 'high';
}

/** Applies renderScale × tier DPR cap via hardware scaling. */
function applyScale(engine: AbstractEngine, tier: QualityTier, renderScale: number): void {
  const dpr = Math.min(window.devicePixelRatio || 1, TIER_CAPS[tier].maxDpr);
  engine.setHardwareScalingLevel(1 / (dpr * renderScale));
}

export class QualityController {
  readonly tier: QualityTier;
  readonly caps: TierCaps;
  renderScale = 1;
  private slowStreak = 0;
  private fastStreak = 0;

  constructor(private readonly engine: AbstractEngine, tier: QualityTier) {
    this.tier = tier;
    this.caps = TIER_CAPS[tier];
    applyScale(engine, tier, 1);
  }

  /** Call once per rendered frame. */
  frame(): void {
    const fps = this.engine.getFps();
    if (fps > 0 && fps < SLOW_FPS) {
      this.slowStreak++;
      this.fastStreak = 0;
    } else if (fps >= FAST_FPS) {
      this.fastStreak++;
      this.slowStreak = 0;
    } else {
      this.slowStreak = Math.max(0, this.slowStreak - 1);
      this.fastStreak = Math.max(0, this.fastStreak - 1);
    }
    if (this.slowStreak >= SUSTAIN_FRAMES && this.renderScale > RENDER_SCALE_MIN) {
      this.renderScale = Math.max(RENDER_SCALE_MIN, this.renderScale - RENDER_SCALE_STEP);
      applyScale(this.engine, this.tier, this.renderScale);
      this.slowStreak = 0;
    } else if (this.fastStreak >= SUSTAIN_FRAMES * 2 && this.renderScale < 1) {
      // Climb back slowly; hysteresis deliberately asymmetric (research §6).
      this.renderScale = Math.min(1, this.renderScale + RENDER_SCALE_STEP);
      applyScale(this.engine, this.tier, this.renderScale);
      this.fastStreak = 0;
    }
  }

  /** Re-apply on window resize. */
  resize(): void {
    applyScale(this.engine, this.tier, this.renderScale);
  }
}
